<template>
  <div class="tw-flex tw-flex-col tw-gap-3">
    <div class="tw-grid tw-grid-cols-2 tw-gap-2">
      <ColumnFieldGroup
        :fields="headerFields"
        :values="column.values"
        :availability="availability"
        @update="onFieldUpdate"
      />
    </div>

    <div class="tw-flex tw-items-stretch tw-gap-2">
      <div
        :class="[operandCardClasses, 'tw-min-w-0 tw-flex-1']"
        data-testid="difference-operand-a"
      >
        <span :class="operandBadgeClasses">{{ t('difference.value_a') }}</span>
        <div class="tw-flex tw-flex-col tw-gap-1.5">
          <ColumnFieldGroup
            :fields="operandAFields"
            :values="column.values"
            :availability="availability"
            @update="onFieldUpdate"
          />
        </div>
      </div>

      <div
        class="
          tw-flex
          tw-shrink-0
          tw-items-center
          tw-text-sm
          tw-font-semibold
          tw-text-sf-neutral-light-2
        "
        aria-hidden="true"
      >
        {{ COMPARISON_OPERATOR }}
      </div>

      <div
        :class="[operandCardClasses, 'tw-min-w-0 tw-flex-1']"
        data-testid="difference-operand-b"
      >
        <span :class="operandBadgeClasses">{{ t('difference.value_b') }}</span>
        <div class="tw-flex tw-flex-col tw-gap-1.5">
          <ColumnFieldGroup
            :fields="operandBFields"
            :values="column.values"
            :availability="availability"
            @update="onFieldUpdate"
          />
        </div>
      </div>
    </div>

    <p
      v-if="caption"
      class="tw-m-0 tw-text-xs tw-italic tw-text-sf-neutral-light-2"
      data-testid="difference-caption"
    >
      {{ caption }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { FieldAvailability } from '../column-constraints'
import type { EditorColumn, FieldSchema } from '../types'
import {
  COMPARISON_OPERATOR,
  SHOW_AS_FIELD_KEY,
  showAsPresentation,
} from '../column-presentation'
import { useColumnEditorI18n } from '../use-column-editor-i18n'
import ColumnFieldGroup from './column-field-group.vue'

// The expanded form of a difference column: it compares two values (B − A), so
// the two operands render as labeled cards with the operator between them,
// rather than as the flat grid the other column types use.
const props =
  defineProps<{
    column: EditorColumn
    fields: FieldSchema[]
    // Computed for the whole column by the row above: an operand being actuals
    // plus budget narrows the basis field that sits above both operands.
    availability: FieldAvailability
  }>()

const emit =
  defineEmits<{
    update: [key: string, value: string]
  }>()

const t = useColumnEditorI18n()

const onFieldUpdate = (key: string, value: string) => emit('update', key, value)

// Groupings taken from the schema rather than from a hardcoded list of field
// keys: anything the server didn't assign to an operand renders above the
// cards, so a field added to the schema can't silently disappear.
const operandAFields = computed(() =>
  props.fields.filter((field) => field.group === 'a')
)
const operandBFields = computed(() =>
  props.fields.filter((field) => field.group === 'b')
)
const headerFields = computed(() =>
  props.fields.filter((field) => field.group !== 'a' && field.group !== 'b')
)

// Explains the subtraction direction, updating with the "show as" choice. A
// mode we have no presentation for gets no caption rather than a wrong one.
const caption = computed(() => {
  const presentation = showAsPresentation(
    props.column.values[SHOW_AS_FIELD_KEY]
  )
  return presentation && t(presentation.captionKey)
})

const operandCardClasses =
  'tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-sf-neutral-lighter-1 tw-p-2.5'

const operandBadgeClasses =
  'tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-md tw-bg-sf-info-light-1 tw-text-[11px] tw-font-bold tw-text-white'
</script>
